"use client";

import { IBaseLabelValue } from "@/interfaces";
import { usePathname, useRouter } from "next/navigation";
import { cn, formatNumberWithDots, generateBillPaymentCallbackUrl } from "@/lib";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import React, { useRef } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedElectricityInfoInquiryResData } from "@/interfaces/inquiries/governmental";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import { useSession } from "next-auth/react";
import { usePayFinesMutation } from "@/hooks";
import { useAppStore } from "@/store";
import { IPayFinesPayload } from "@/interfaces/inquiries/vehicle";
import { useToastStore } from "@/store/toastStore";
import { BdCopy } from "solar-icon-react/bd";

const ElectricityBillInfoInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedElectricityInfoInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const { data: session } = useSession();
  const {
    isPending: isPaySubmitting,
    mutate: PayFineFn,
    error: paySubmittingError,
    reset: resetPay,
  } = usePayFinesMutation(session?.user.token);
  const router = useRouter();
  const response = data.data.data;
  if (response.message) {
    return (
      <div className="shadow-main relative w-full rounded-2xl bg-white p-8">
        <div
          className="mx-auto max-w-[500px] rounded-lg p-4 text-center text-white"
          style={{ backgroundColor: "#5a4bd2" }}
        >
          {response.message}
        </div>
      </div>
    );
  }
  if (!response) {
    return (
      <div className="shadow-main relative w-full rounded-2xl bg-white p-8">
        <div
          className="mx-auto max-w-[500px] rounded-lg p-4 text-center text-white"
          style={{ backgroundColor: "#5a4bd2" }}
        >
          برای این شناسه قبضی صادر نشده
        </div>
      </div>
    );
  }
  const { bill_id } = data.data.input;

  const inputContent: IBaseLabelValue[] = [
    {
      label: t("electricityBill.inputLabel"),
      value: response.billID || "",
    },
  ];
  const formatPrice = (value?: string | number) =>
    value ? `${formatNumberWithDots(Number(value) / 10)} تومان` : "-";

  const resultContent: IBaseLabelValue[] = [
    {
      label: t("electricityBill.companyName"),
      value: response.info.CompanyName || "-",
    },
    {
      label: t("electricityBill.customerName"),
      value: response.info.CustomerName || "-",
    },
    {
      label: t("electricityBill.customerFamily"),
      value: response.info.CustomerFamily || "-",
    },
    {
      label: t("electricityBill.customerType"),
      value: response.info.CustomerType || "-",
    },
    {
      label: t("electricityBill.address"),
      value: response.info.Address || "-",
    },
    {
      label: t("electricityBill.postalCode"),
      value: response.info.PostalCode || "-",
    },
    {
      label: t("electricityBill.fileNumber"),
      value: response.info.FileNumber || "-",
    },
    {
      label: t("electricityBill.identificationNumber"),
      value: response.info.IdentificationNumber || "-",
    },
    {
      label: t("electricityBill.tariffType"),
      value: response.info.TariffType || "-",
    },
    {
      label: t("electricityBill.phase"),
      value: response.info.Phase ? `${response.info.Phase} فاز` : "-",
    },
    {
      label: t("electricityBill.amper"),
      value: response.info.Amper ? `${response.info.Amper} آمپر` : "-",
    },
    {
      label: t("electricityBill.voltageType"),
      value: response.info.VoltageType || "-",
    },
    {
      label: t("electricityBill.contractDemand"),
      value: response.info.ContractDemand || "-",
    },
    {
      label: t("electricityBill.year"),
      value: response.info.Year || "-",
    },
    {
      label: t("electricityBill.period"),
      value: response.info.Period || "-",
    },
    {
      label: t("electricityBill.previousReadingDate"),
      value: response.info.PreviousReadingDate || "-",
    },
    {
      label: t("electricityBill.currentReadingDate"),
      value: response.info.CurrentReadingDate || "-",
    },
    {
      label: t("electricityBill.billExportationDate"),
      value: response.info.BillExportationDate || "-",
    },
    {
      label: t("electricityBill.rejectDate"),
      value: response.info.RejectDate || "-",
    },
    {
      label: t("electricityBill.normalConsumption"),
      value: response.info.NormalConsumption || "-",
    },
    {
      label: t("electricityBill.peakConsumption"),
      value: response.info.PeakConsumption || "-",
    },
    {
      label: t("electricityBill.lowConsumption"),
      value: response.info.LowConsumption || "-",
    },
    {
      label: t("electricityBill.fridayConsumption"),
      value: response.info.FridayConsumption || "-",
    },
    {
      label: t("electricityBill.reactiveConsumption"),
      value: response.info.ReactiveConsumption || "-",
    },
    {
      label: t("electricityBill.demandRead"),
      value: response.info.DemandRead || "-",
    },
    {
      label: t("electricityBill.averageConsumption"),
      value: response.info.AverageConsumption || "-",
    },
    {
      label: t("electricityBill.billPayableAmount"),
      value: formatPrice(response.info.BillPayableAmount),
    },
    {
      label: t("electricityBill.periodAmount"),
      value: formatPrice(response.info.PeriodAmount),
    },
    {
      label: t("electricityBill.insuranceAmount"),
      value: formatPrice(response.info.InsuranceAmount),
    },
    {
      label: t("electricityBill.taxAmount"),
      value: formatPrice(response.info.TaxAmount),
    },
    {
      label: t("electricityBill.payTollAmount"),
      value: formatPrice(response.info.PaytollAmount),
    },
    {
      label: t("electricityBill.electricityTaxAmount"),
      value: formatPrice(response.info.ElectricityTaxAmount),
    },
    {
      label: t("electricityBill.previousDebt"),
      value: formatPrice(response.info.PreviousDebt),
    },
    {
      label: t("electricityBill.energyAmount"),
      value: formatPrice(response.info.EnergyAmount),
    },
    {
      label: t("electricityBill.reactiveAmount"),
      value: formatPrice(response.info.ReactiveAmount),
    },
    {
      label: t("electricityBill.demandAmount"),
      value: formatPrice(response.info.DemandAmount),
    },
    {
      label: t("electricityBill.subscriptionAmount"),
      value: formatPrice(response.info.SubscriptionAmount),
    },
    {
      label: t("electricityBill.seasonAmount"),
      value: formatPrice(response.info.SeasonAmount),
    },
    {
      label: t("electricityBill.freeAmount"),
      value: formatPrice(response.info.FreeAmount),
    },
    {
      label: t("electricityBill.gasDiscountAmount"),
      value: formatPrice(response.info.GasDiscountAmount),
    },
    {
      label: t("electricityBill.discountAmount"),
      value: formatPrice(response.info.DiscountAmount),
    },
    {
      label: t("electricityBill.warmDaysCount"),
      value: response.info.WarmDaysCount || "-",
    },
    {
      label: t("electricityBill.coldDaysCount"),
      value: response.info.ColdDaysCount || "-",
    },
    {
      label: t("electricityBill.totalDaysCount"),
      value: response.info.TotalDaysCount || "-",
    },
    {
      label: t("electricityBill.consumptionDebtAmount"),
      value: formatPrice(response.info.ConsumptionDebtAmount),
    },
    {
      label: t("electricityBill.otherDebtAmount"),
      value: formatPrice(response.info.OtherDebtAmount),
    },
    {
      label: t("electricityBill.branchDebtAmount"),
      value: formatPrice(response.info.BranchDebtAmount),
    },
    {
      label: t("electricityBill.amount"),
      value: formatPrice(response.amount),
    },
    {
      label: t("electricityBill.inputLabel"),
      value: response.billID || "",
      type: "copiable",
    },
    {
      label: t("electricityBill.payID"),
      value: response.payID || "",
      type: "copiable",
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  const handlePayBillSubmission = () => {
    const payload: IPayFinesPayload = {
      amount: response.amount,
      callback_url: generateBillPaymentCallbackUrl(pathName, (data.data as any).purchase_id),
      bill_id: Number(response.billID),
      pay_id: Number(response.payID),
    };
    PayFineFn(
      { payload },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathName);
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };
  const { setOpenToast } = useToastStore();
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setOpenToast({
        variant: "default",
        message: "کپی شد",
      });
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };
  return (
    <div className="shadow-main relative basis-[500px] rounded-2xl bg-linear-to-b from-rose-500 to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          استعلام قبض برق با جزئیات
        </h2>

        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("postalCodeInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            id="electricity-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`قبض برق با جزئیات- ${bill_id}`}
          />
        </FlexBetween>
        <ul
          className={cn(
            "shadow-main mb-10 flex flex-col gap-6 rounded-xl p-4 py-8 sm:p-8",
          )}
        >
          {resultContent.map((item, index) => (
            <li
              key={index}
              className={cn(
                "flex-between items-start gap-6 text-xs sm:text-sm md:gap-8 lg:text-base",
                item.value === "-" ? "hidden" : "flex",
              )}
            >
              <span className="min-w-max">{item.label}</span>
              <div className="flex items-center gap-1">
                <span className="text-start" style={{ color: "#5a2bd2" }}>
                  {item.value}
                </span>
                {item.type === "copiable" && (
                  <BdCopy
                    onClick={() => copy(item.value)}
                    className="text-primary-main cursor-pointer text-base"
                  />
                )}
              </div>
            </li>
          ))}
          {response?.amount != 0 ? (
            <CustomButton
              id="details-electricity-inquiry-back"
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
              onClick={handlePayBillSubmission}
              isLoading={isPaySubmitting}
            >
              پرداخت {formatPrice(response.amount)}
            </CustomButton>
          ) : (
            <p className="bg-success-main mx-auto w-full max-w-[200px] rounded-lg px-3 py-2 text-center text-white">
              تسویه شده
            </p>
          )}
        </ul>
        <FlexCenter className="mt-4 flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="electricity-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`قبض برق با جزئیات- ${bill_id}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-electricity-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-electricity-inquiry-new-request"
              onClick={() =>
                router.push(
                  routes.services.Electricity_Bill_Info_inquiry.rewritePath,
                )
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle="اطلاعات قبض برق با جزئیات"
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default ElectricityBillInfoInquiryDetails;
